import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  AppBar as DialogAppBar,
  Toolbar,
  Dialog,
  DialogContent,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import {
  Close as CloseIcon,
  Delete,
  Check as CheckIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import { useWorkspace } from "../../utils/WorkspaceContext";
import { DEFAULT_CATEGORY } from "../../utils/constants";
import ActionItemDialog from "./ActionItemDialog";
import ActionItemTemplateDialog from "../dialogs/ActionItemTemplateDialog";
import ActionItemsTable from "./ActionItemsTable";
import TaskFormFields, { RecurrenceControl } from "./TaskFormFields";
import { useTaskOverlay } from "../../utils/TaskOverlayContext";
import useActionItems from "../../hooks/useActionItems";
import useTaskFieldPatcher from "../../hooks/useTaskFieldPatcher";

// Fields a `prefill` may seed. `epic` and `project` are not task columns — they
// scope the Track picker — but they travel with the rest so a duplicated task
// lands on the same track.
const PREFILL_FIELDS = [
  "epic",
  "project",
  "track",
  "sprint_slot",
  "department",
  "division",
  "category",
  "task_class",
  "line_up",
  "status",
  "do_date",
  "start_time",
  "end_time",
  "assignee",
  "reviewer",
];

// Fields the pickers scope by but that are not task columns, so they must never
// reach the API.
const NON_TASK_FIELDS = ["epic", "project"];

/** First defined value, treating "" as undefined. */
const firstOf = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && v !== "");

/**
 * Everything a task carries except the three fields a duplicate must not
 * inherit: name, notes and its action items.
 *
 * Reads both the detail shape (`department`) and the list shape
 * (`department_id`) — the nested/table endpoints emit raw FK columns under
 * `*_id`, so keying only off the detail names silently dropped the department,
 * division and track of any task duplicated from a list view.
 */
export const duplicatePrefillFromTask = (task) => ({
  project: firstOf(task.project_id, task.project),
  track: firstOf(task.track_id, task.track),
  sprint_slot: task.sprint_slot ?? "current",
  department: firstOf(task.department_id, task.department),
  division: firstOf(task.division_id, task.division),
  category: task.category ?? undefined,
  task_class: task.task_class ?? undefined,
  line_up: firstOf(task.line_up, task.lineUp),
  status: task.status ?? undefined,
  do_date: task.do_date ?? undefined,
  start_time: task.start_time ?? undefined,
  end_time: task.end_time ?? undefined,
  // List serializers nest assignee/reviewer as objects; details send plain ids.
  assignee: firstOf(task.assignee?.id, task.assignee),
  reviewer: firstOf(task.reviewer?.id, task.reviewer),
});

const emptyTask = (workspaceId) => ({
  name: "",
  notes: "",
  category: DEFAULT_CATEGORY,
  task_class: "Core",
  line_up: 30,
  sprint_slot: "current", // New tasks default to the current slot.
  status: "To Start",
  do_date: null,
  start_time: null,
  end_time: null,
  project: "",
  track: "",
  assignee: "",
  reviewer: "",
  division: null,
  department: null,
  workspace: workspaceId,
});

// Fields that must be filled before the task is auto-created.
const REQUIRED_FIELDS = [
  "name",
  "assignee",
  "project",
  "track",
  "sprint_slot",
  "department",
  "division",
  "category",
];

const REQUIRED_MESSAGES = {
  name: "Task name is required",
  assignee: "Required",
  project: "Project is required",
  track: "Track is required",
  department: "Department is required",
  division: "Division is required",
  category: "Category is required",
};

/**
 * Task creation surface. Renders the shared task form, so the field order and
 * placement match the task detail page exactly.
 *
 * The task is created as soon as every required field is filled, then edited in
 * place. Three things make that safe, and each replaces a specific failure:
 *
 *  - creation is guarded by a ref, not by render state. The old `loading` /
 *    `created` guards were read from a closure captured before the POST was
 *    even sent, and the debounced creator was rebuilt on every render (its memo
 *    depended on the caller's inline `onTaskCreated`), so timers armed by
 *    earlier renders stayed live and could each POST a task.
 *  - edits made while the POST is in flight are replayed once the id arrives,
 *    instead of being dropped on the floor.
 *  - the prefill is applied before the option lists can prune it, so a seeded
 *    project/track/sprint no longer flickers away as those lists resolve.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => void} props.onTaskCreated
 * @param {Object} [props.prefill] Any of {@link PREFILL_FIELDS}; used by the
 *   "add task here" buttons and by Duplicate Task.
 */
const TaskCreate = ({ open, onClose, onTaskCreated, prefill }) => {
  const { currentWorkspace } = useWorkspace();
  const { displayMode } = useTaskOverlay();

  const [task, setTask] = useState(() => emptyTask(currentWorkspace?.id));
  const [epicFilter, setEpicFilter] = useState("");
  const [createdId, setCreatedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  // Blocks the option-list effects from pruning a seeded project/track until
  // the prefill has actually been applied.
  const [prefillReady, setPrefillReady] = useState(false);

  // A field is only flagged once the user has touched it.
  const [touched, setTouched] = useState({});

  const [projects, setProjects] = useState([]);
  const [tracks, setTracks] = useState([]);
  // Every workspace track, so picking a division can find its track/project.
  const [allTracks, setAllTracks] = useState([]);
  const [epics, setEpics] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedActionItem, setSelectedActionItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [saveError, setSaveError] = useState("");

  const created = createdId != null;

  // Guards + queues that must survive re-renders untouched.
  const createStateRef = useRef("idle"); // idle | inflight | done
  const createTimerRef = useRef(null);
  const taskRef = useRef(task);
  taskRef.current = task;
  // Field edits made between "POST sent" and "id received".
  const queuedEditsRef = useRef({});
  const onTaskCreatedRef = useRef(onTaskCreated);
  onTaskCreatedRef.current = onTaskCreated;

  const missing = REQUIRED_FIELDS.filter((f) => !task[f]);
  // A track from another division is flagged by the form; never create one.
  const selectedTrack = allTracks.find((t) => String(t.id) === String(task.track));
  const trackMismatch = Boolean(
    selectedTrack?.division != null &&
      task.division != null &&
      String(selectedTrack.division) !== String(task.division)
  );
  const errors = Object.fromEntries(
    missing.filter((f) => touched[f]).map((f) => [f, true])
  );

  const { patchField, flush: flushFieldPatches, mergeResponse } =
    useTaskFieldPatcher({
      taskId: createdId,
      workspaceId: currentWorkspace?.id,
      onPendingChange: setHasPendingChanges,
      onPatched: (data) =>
        setTask((prev) => mergeResponseRef.current(prev, data)),
      onError: (error) =>
        setSaveError(
          error?.response?.data?.detail || "That change could not be saved."
        ),
    });
  const mergeResponseRef = useRef(mergeResponse);
  mergeResponseRef.current = mergeResponse;

  const {
    items: actionItems,
    setItems: setActionItems,
    addItem: handleAddActionItem,
    updateItem: handleActionItemUpdate,
    removeItem: removeActionItem,
    reload: reloadActionItems,
    clearInvalidTypes: clearInvalidActionItemTypes,
  } = useActionItems(createdId, {
    workspaceId: currentWorkspace?.id,
    onPendingChange: setHasPendingChanges,
  });

  // Reset everything when the dialog opens.
  useEffect(() => {
    if (!open) return;
    setTask(emptyTask(currentWorkspace?.id));
    setEpicFilter("");
    setCreatedId(null);
    setCreating(false);
    setActionItems([]);
    setTracks([]);
    setHasPendingChanges(false);
    setTouched({});
    setPrefillReady(false);
    setSaveError("");
    createStateRef.current = "idle";
    queuedEditsRef.current = {};
    if (createTimerRef.current) {
      clearTimeout(createTimerRef.current);
      createTimerRef.current = null;
    }
  }, [open, currentWorkspace?.id, setActionItems]);

  // Workspace-level option lists.
  useEffect(() => {
    if (!open || !currentWorkspace?.id) return;
    let cancelled = false;
    Promise.all([
      api.get("epics/", { params: { workspace_id: currentWorkspace.id } }),
      api.get("divisions/", { params: { workspace_id: currentWorkspace.id } }),
      api.get("departments/", { params: { workspace_id: currentWorkspace.id } }),
      api.get("tracks/", { params: { workspace_id: currentWorkspace.id } }),
    ])
      .then(([epicsRes, divisionsRes, departmentsRes, tracksRes]) => {
        if (cancelled) return;
        setEpics(epicsRes.data || []);
        setDivisions(divisionsRes.data || []);
        setDepartments(departmentsRes.data || []);
        setAllTracks(tracksRes.data || []);
      })
      .catch((err) => console.error("Error fetching task form options:", err));
    return () => {
      cancelled = true;
    };
  }, [open, currentWorkspace?.id]);

  // Apply the caller's prefill before anything can prune it.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const apply = async () => {
      if (!prefill) {
        if (!cancelled) setPrefillReady(true);
        return;
      }

      // A task only stores its track, so a caller can hand us a track without
      // the project above it. Resolve it first — the Track picker is scoped by
      // project, and an unscoped track would be dropped as invalid.
      let project = prefill.project;
      if (!project && prefill.track) {
        try {
          const res = await api.get(`tracks/${prefill.track}/`, {
            params: { workspace_id: currentWorkspace?.id },
          });
          project = res.data?.payload?.project ?? res.data?.project;
        } catch (error) {
          console.error("Error resolving the prefilled track's project:", error);
        }
      }
      if (cancelled) return;

      const seeded = {};
      PREFILL_FIELDS.forEach((field) => {
        const value = field === "project" ? project : prefill[field];
        if (value === undefined || value === null || value === "") return;
        if (field === "epic") {
          setEpicFilter(String(value));
        } else if (field === "project" || field === "track") {
          // The pickers hold these as strings so their MenuItem values match.
          seeded[field] = String(value);
        } else {
          seeded[field] = value;
        }
      });
      setTask((prev) => ({ ...prev, ...seeded }));
      setPrefillReady(true);
    };

    apply();
    return () => {
      cancelled = true;
    };
  }, [open, prefill, currentWorkspace?.id]);

  // Projects, narrowed by the selected epic.
  useEffect(() => {
    if (!open || !currentWorkspace?.id) return;
    let cancelled = false;
    const params = { workspace_id: currentWorkspace.id };
    if (epicFilter) params.epic_id = epicFilter;
    api
      .get("/filtered-projects/", { params })
      .then((res) => {
        if (cancelled) return;
        const next = Array.isArray(res.data) ? res.data : res.data?.payload || [];
        setProjects(next);
        // Drop a selection the new filter excludes — but never before the
        // prefill has landed, or a seeded project is cleared the moment this
        // list resolves.
        if (!prefillReady) return;
        setTask((prev) =>
          prev.project && !next.some((p) => String(p.id) === String(prev.project))
            ? { ...prev, project: "", track: "" }
            : prev
        );
      })
      .catch((error) =>
        console.error("Error fetching filtered projects:", error)
      );
    return () => {
      cancelled = true;
    };
  }, [epicFilter, currentWorkspace?.id, open, prefillReady]);

  // Tracks belonging to the selected project.
  useEffect(() => {
    if (!open || !task.project) {
      setTracks([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`filtered-tracks/${task.project}/`, {
        params: { workspace_id: currentWorkspace.id },
      })
      .then((res) => {
        if (cancelled) return;
        const next = res.data.payload || [];
        setTracks(next);
        if (!prefillReady) return;
        setTask((prev) =>
          prev.track && !next.some((t) => String(t.id) === String(prev.track))
            ? { ...prev, track: "" }
            : prev
        );
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error fetching tracks:", error);
        setTracks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [task.project, open, currentWorkspace?.id, prefillReady]);

  // --- creation -----------------------------------------------------------

  const patchFieldRef = useRef(patchField);
  patchFieldRef.current = patchField;

  const createTask = useCallback(async () => {
    if (createStateRef.current !== "idle") return;
    createStateRef.current = "inflight";
    setCreating(true);
    setHasPendingChanges(true);

    // `project` and `epic` only scope the pickers — the task's project is
    // implied by its track, so they never reach the API.
    const payload = { ...taskRef.current };
    NON_TASK_FIELDS.forEach((field) => delete payload[field]);

    try {
      const response = await api.post("tasks/", payload);
      const createdTask = response.data;
      createStateRef.current = "done";
      // Merge the server's representation (it carries the derived sprint_slot
      // and sprint_window the Sprint picker renders from) under anything the
      // user changed while the POST was out.
      const queued = queuedEditsRef.current;
      queuedEditsRef.current = {};
      setTask((prev) => ({
        ...prev,
        ...createdTask,
        // `track` is held as a string here so it matches the picker's option
        // values; the serializer sends it back as a number.
        track: createdTask.track != null ? String(createdTask.track) : prev.track,
        ...queued,
      }));
      setCreatedId(createdTask.id);

      // Persist edits made during the flight; they were never part of the POST.
      Object.entries(queued).forEach(([field, value]) => {
        if (NON_TASK_FIELDS.includes(field)) return;
        patchFieldRef.current(field, value);
      });

      if (createdTask.id) {
        try {
          // Seed the task with a single action item mirroring the task itself.
          await api.post("action-items/", {
            description: "same as task",
            task: createdTask.id,
            is_completed: false,
            read: false,
            is_reviewed: false,
            seq: 1,
          });
          const items = await api.get(`tasks/${createdTask.id}/action-items/`, {
            params: { workspace_id: currentWorkspace.id },
          });
          setActionItems(items.data?.payload || []);
        } catch (aiError) {
          console.error("Error creating default action item:", aiError);
        }
      }

      setHasPendingChanges(false);
      onTaskCreatedRef.current?.();
    } catch (error) {
      console.error("Error creating task:", error);
      // Say why. Without this the dialog just sat there looking unsaved while
      // it silently re-attempted the same rejected POST on every edit.
      const detail = error?.response?.data;
      setSaveError(
        detail?.detail ||
          (detail && typeof detail === "object"
            ? Object.entries(detail)
                .map(([field, msgs]) => `${field}: ${[].concat(msgs).join(", ")}`)
                .join(" · ")
            : "") ||
          "The task could not be created."
      );
      // Let the user correct the input and try again.
      createStateRef.current = "idle";
      setHasPendingChanges(false);
    } finally {
      setCreating(false);
    }
  }, [currentWorkspace, setActionItems]);

  // Auto-create once every required field is filled, after a short settle so we
  // don't fire mid-typing. Re-armed on each change; the ref guard above means
  // only one POST can ever go out regardless of how often this runs.
  useEffect(() => {
    if (!open || created || !prefillReady) return undefined;
    if (createStateRef.current !== "idle") return undefined;
    if (missing.length > 0 || trackMismatch) return undefined;

    createTimerRef.current = setTimeout(createTask, 800);
    return () => {
      if (createTimerRef.current) clearTimeout(createTimerRef.current);
      createTimerRef.current = null;
    };
  }, [open, created, prefillReady, missing.length, trackMismatch, task, createTask]);

  // Attaching a repeat rule can set the task's do_date (and so its sprint), so
  // pull those back along with the rule.
  const reloadRecurrence = useCallback(async () => {
    if (!createdId) return;
    try {
      const res = await api.get(`tasks/${createdId}/`, {
        params: { workspace_id: currentWorkspace?.id },
      });
      const { recurrence, do_date, sprint_slot, sprint_window } = res.data;
      setTask((prev) => ({ ...prev, recurrence, do_date, sprint_slot, sprint_window }));
    } catch (error) {
      console.error("Error reloading the task's recurrence:", error);
    }
  }, [createdId, currentWorkspace?.id]);

  // `project` and `epic` scope the pickers rather than living on the task, so
  // they never reach the API.
  const handleFieldChange = useCallback(
    (field, value) => {
      setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

      if (field === "epic") {
        setEpicFilter(value);
        return;
      }
      setTask((prev) => {
        const next = { ...prev, [field]: value };
        // Changing the project invalidates the track beneath it.
        if (field === "project" && String(prev.project) !== String(value)) {
          next.track = "";
        }
        return next;
      });

      if (NON_TASK_FIELDS.includes(field)) return;

      // Action-item Types are constrained by the task's category, so a category
      // change has to drop the ones it just invalidated.
      if (field === "category") clearInvalidActionItemTypes(value);

      if (createStateRef.current === "done") {
        patchField(field, value);
      } else if (createStateRef.current === "inflight") {
        // The POST body is already sealed; replay this once the id lands.
        queuedEditsRef.current[field] = value;
      }
    },
    [patchField, clearInvalidActionItemTypes]
  );

  // The assignee is never auto-populated: a new task starts unassigned and the
  // user picks who it belongs to. Only an explicit `prefill` (Duplicate Task,
  // "add task here") seeds it.

  // --- action items -------------------------------------------------------

  const handleContextMenu = (event, actionItem) => {
    event.preventDefault();
    setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
    setSelectedActionItem(actionItem);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedActionItem(null);
  };

  const handleDeleteActionItem = async () => {
    const key = selectedActionItem?.id ?? selectedActionItem?.tempId;
    if (key == null) return;
    await removeActionItem(key);
    handleCloseContextMenu();
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
    reloadActionItems();
  };

  // Send anything still queued before the surface closes.
  const handleClose = useCallback(() => {
    flushFieldPatches();
    onClose?.();
  }, [flushFieldPatches, onClose]);

  if (!open) return null;

  // In "drawer" mode both create and detail surfaces open as a 60% right-side
  // drawer; otherwise keep the legacy full-screen dialog.
  const Container = displayMode === "drawer" ? Drawer : Dialog;
  const containerProps =
    displayMode === "drawer"
      ? {
          anchor: "right",
          open,
          onClose: handleClose,
          PaperProps: { sx: { width: { xs: "100%", md: "60%" } } },
        }
      : { fullScreen: true, open, onClose: handleClose };

  return (
    <Container {...containerProps}>
      <DialogAppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            {created ? `Editing: ${task.name}` : "Create New Task"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {created && !hasPendingChanges && !creating ? (
              <CheckIcon sx={{ color: "success.main", mr: 1 }} />
            ) : (
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: "warning.main",
                  mr: 1,
                }}
              />
            )}
            <Button autoFocus color="inherit" onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Toolbar>
      </DialogAppBar>
      <DialogContent>
        <Box sx={{ p: 1, margin: "0 auto", mt: 2 }}>
          {/* No `actionItemsSlot` here: while creating, the classification
              fields are the work, so the action items stay at the bottom. */}
          <TaskFormFields
            values={{ ...task, epic: epicFilter }}
            actionItems={actionItems}
            options={{ epics, projects, tracks, allTracks, departments, divisions }}
            errors={errors}
            helperText={REQUIRED_MESSAGES}
            onChange={handleFieldChange}
            recurrenceSlot={
              <RecurrenceControl
                taskId={createdId}
                workspaceId={currentWorkspace?.id}
                value={task.recurrence}
                doDate={task.do_date}
                onSaved={reloadRecurrence}
              />
            }
          />

          <Divider sx={{ my: 2 }} />
          <Button
            variant="outlined"
            onClick={() => setTemplateDialogOpen(true)}
            disabled={!created}
          >
            Select Task Template
          </Button>
          <ActionItemTemplateDialog
            open={templateDialogOpen}
            onClose={handleCloseTemplateDialog}
            taskId={createdId}
          />

          <Box sx={{ mt: 2, width: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Action Items
            </Typography>
            {!created && (
              <Typography variant="caption" color="textSecondary">
                (Disabled until task is created)
              </Typography>
            )}
            <ActionItemsTable
              actionItems={actionItems}
              category={task.category}
              onUpdateItem={handleActionItemUpdate}
              onAddItem={handleAddActionItem}
              onOpenItem={(item) => {
                setSelectedActionItem(item);
                setIsDialogOpen(true);
              }}
              onContextMenu={handleContextMenu}
              compact={displayMode === "drawer"}
              disabled={!created}
            />
          </Box>
        </Box>
      </DialogContent>

      <ActionItemDialog
        open={isDialogOpen}
        actionItemId={selectedActionItem?.id}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedActionItem(null);
          reloadActionItems();
        }}
      />
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={8000}
        onClose={() => setSaveError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSaveError("")} sx={{ width: "100%" }}>
          {saveError}
        </Alert>
      </Snackbar>
      <Menu
        open={Boolean(contextMenu)}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={handleDeleteActionItem}
          sx={{ color: "error.main", "&:hover": { bgcolor: "error.light" } }}
        >
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Delete Action Item
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default TaskCreate;
